/**
 * Assignments — giving one person one course or one library, inside one academy.
 *
 *   GET    /api/authoring/v1/tenants/<slug>/people/<username>/assignments
 *   POST   /api/authoring/v1/tenants/<slug>/people/<username>/assignments        { id, level }
 *   DELETE /api/authoring/v1/tenants/<slug>/people/<username>/assignments/<id>
 *
 * WHAT AN ASSIGNMENT ACTUALLY IS. Nothing is stored for it. A course assignment is a place
 * on that course's team (Open edX's own `staff` / `instructor` roles) and a library
 * assignment is a per-library access grant (`read` / `author` / `admin`) — the same two
 * primitives Studio's Course Team screen and the library team API write. Which is why this
 * changes what the person sees without anything here filtering anything: Studio's course
 * listing is already filtered by those roles, and our tenant library listing already falls
 * back to that grant for anyone without an academy-wide role.
 *
 * THE SERVER IS THE BOUNDARY. It re-checks that the caller administers this academy and
 * that the course or library belongs to it — the organisation is read off the key and
 * matched against the tenant, never taken from what we send. Everything below only decides
 * what the page offers and how it reads.
 *
 * ONE ROUTE, TWO KINDS. The identifier says whether it is a course (`course-v1:…`) or a
 * library (`lib:…`), so there is no `type` field for a client to get out of step with the
 * key beside it.
 */

import { openedx, ApiError } from './openedx';

/** What kind of thing was assigned. Decided by the server from the key. */
export type AssignmentKind = 'course' | 'library';

/** A place on a course team, weakest first. Open edX's own vocabulary. */
export type CourseLevel = 'staff' | 'instructor';

/** Per-library access, weakest first. `ContentLibraryPermission.ACCESS_LEVEL_CHOICES`. */
export type LibraryLevel = 'read' | 'author' | 'admin';

export type AssignmentLevel = CourseLevel | LibraryLevel;

export interface Assignment {
  kind: AssignmentKind;
  /** Course key or library key — what the URL and every support ticket will call it. */
  id: string;
  title: string;
  level: AssignmentLevel;
}

export interface PersonAssignments {
  username: string;
  /**
   * They already hold an academy-wide role.
   *
   * Which means assignments do nothing for them: they can already see every course and
   * every library here. The page has to say so — offering a control that cannot change
   * anything is worse than offering none.
   */
  hasOrgRole: boolean;
  courses: Assignment[];
  libraries: Assignment[];
}

const base = (slug: string, username: string) =>
  `/api/authoring/v1/tenants/${encodeURIComponent(slug)}/people/${encodeURIComponent(username)}/assignments`;

export const listAssignments = (slug: string, username: string) =>
  openedx.get<PersonAssignments>(base(slug, username));

/**
 * Give them one course or one library, at `level`.
 *
 * Also the way to CHANGE a level: sending a different one for something they already hold
 * moves them to it. That is what lets the page put a level picker next to an existing
 * assignment instead of making an administrator remove and re-add it.
 */
export const assign = (slug: string, username: string, id: string, level: AssignmentLevel) =>
  openedx.post<Assignment>(base(slug, username), { id, level });

/** Take one back. The course, the library and their account are all untouched. */
export const unassign = (slug: string, username: string, id: string) =>
  // The key goes in RAW, as it does for `/api/authoring/v1/editor/<usage key>`: the server
  // matches this segment loosely because keys carry ':' and '+', and both are legal in a
  // path segment. Percent-encoding them would only be undone before the pattern is matched.
  openedx.delete<void>(`${base(slug, username)}/${id}`);

/* ------------------------------------------------------------------ vocabulary ---- */

/**
 * WHAT THEY WILL BE ABLE TO DO, not what the role is called.
 *
 * An administrator choosing a level is deciding how much of somebody's day this opens up.
 * "Instructor" and "author" answer a question they did not ask; these sentences answer the
 * one they did. The role names still exist — they are what the platform stores and what
 * Studio will show — but they are never the thing being chosen from.
 */
export const COURSE_LEVELS: CourseLevel[] = ['staff', 'instructor'];
export const LIBRARY_LEVELS: LibraryLevel[] = ['read', 'author', 'admin'];

const LEVEL_LABELS: Record<AssignmentLevel, string> = {
  staff: 'Build and edit the course',
  instructor: 'Build it and manage who else works on it',
  read: 'Look at the content, change nothing',
  author: 'Add and edit content',
  admin: 'Edit content and manage who else works on it',
};

const LEVEL_BLURBS: Record<AssignmentLevel, string> = {
  staff:
    'They can open this course, write and rearrange its content, and publish it. They cannot add or remove other people from it.',
  instructor:
    'Everything above, plus they can give other people access to this course and take it away again — including from you.',
  read: 'They can open this library and read what is in it. Nothing they do can change it.',
  author:
    'They can add, edit and delete content in this library. They cannot change who else has access to it.',
  admin:
    'Everything above, plus they can give other people access to this library and take it away again — including from you.',
};

export const levelLabel = (level: string): string =>
  LEVEL_LABELS[level as AssignmentLevel] ?? level;

export const levelBlurb = (level: string): string =>
  LEVEL_BLURBS[level as AssignmentLevel] ?? '';

/** Options for a picker, in the order they widen access. */
export const levelOptions = (kind: AssignmentKind): [string, string][] =>
  (kind === 'course' ? COURSE_LEVELS : LIBRARY_LEVELS).map((level) => [level, levelLabel(level)]);

/**
 * Where an assignment goes in the admin app, so the row is a way in and not just a name.
 * Keys carry ':' and '+', so they encode.
 */
export const assignmentHref = (item: Assignment): string =>
  item.kind === 'course'
    ? `/admin/courses/${encodeURIComponent(item.id)}`
    : `/admin/libraries/${encodeURIComponent(item.id)}`;

/* ---------------------------------------------------------------------- errors ---- */

export interface AssignmentError {
  status: number;
  detail: string | null;
}

/**
 * The server's own sentence, when it sent one.
 *
 * Every refusal here is written for the person reading it — "That course does not belong
 * to this academy", "They are the last person who can manage this course's team" — so it
 * is repeated rather than translated from a status code.
 */
export function assignmentError(error: unknown): AssignmentError {
  if (!(error instanceof ApiError)) return { status: 0, detail: null };
  const body = (error.body ?? {}) as { detail?: unknown };
  return {
    status: error.status,
    detail: typeof body.detail === 'string' ? body.detail : null,
  };
}
